/**
 * One source of brand truth.
 *
 * UIKit deliberately ships no <Logo>, no <Header> and no <Footer> — product
 * identity is the one thing a component library cannot get right for you.
 * It lives here instead, so there is exactly one place to change it.
 *
 * The theme id must match the class in `styles/brand.css`.
 */
/**
 * The brand, and the whole product.
 *
 * This was one module of five, labelled "Intake" beside the wordmark. It is
 * now the product itself — one thing a firm buys, at naumene.com — so the
 * label is empty and the wordmark stands alone. The field stays because the
 * lockup still reads it, and because a future module would need it back.
 */
const PLATFORM = 'Naumene';

/** Empty: there is one product, and its name is the brand. */
const MODULE = '';

export const brand = {
  /** Theme id — matches `.theme-naumene` in styles/brand.css. */
  themeId: 'naumene',

  /** The brand alone, for the lockup. Never includes the module. */
  name: PLATFORM,

  /** The module label. Rendered as an eyebrow beside the brand, never in prose. */
  module: MODULE,

  /** For running text and the browser tab. */
  productName: MODULE ? `${PLATFORM} ${MODULE}` : PLATFORM,

  tagline: 'Accelerating legal practice.',  // Mishu_Brand_Kit.html v1.0

  /** Shown in the shell and the browser tab. */
  shortName: MODULE ? `${PLATFORM} ${MODULE}` : PLATFORM,

  /**
   * Primary navigation. Empty by default — a one-page scaffold has nowhere to
   * navigate to, and a lone "Home" link pointing at the current page is noise.
   * Add entries and the header (and the mobile slide-over) pick them up:
   *
   *   nav: [{ label: 'Pricing', to: '/pricing' }],
   */
  /*
   * Marketing nav — content first, actions elsewhere.
   *
   * Pricing leads because the number is the differentiator — the same unit
   * everyone in this category bills in, at $29 against Clio Grow's $59 — and
   * it is the first thing a firm shopping this comes to check. Compare follows
   * it: buyers searching "<competitor>
   * alternative" are further down the funnel than almost anyone else who
   * lands here.
   *
   * About moved to the footer. It does not convert a B2B buyer, and every row
   * kept here makes the two that do matter harder to see.
   *
   * Features leads because it is the question asked first — what does it do —
   * and because it is the only page a salesperson can send that answers "does
   * it have X" without a scroll.
   *
   * Partners is in the nav now that the page exists. It earns the row on
   * distribution rather than traffic: one reseller with twenty practices is
   * twenty firms we never had to sell to one at a time, and that reader will
   * not go looking for a link in the footer.
   *
   * Still to build before it can be added: Resources. Deliberately absent
   * rather than stubbed — a nav link to a page that does not exist is worse
   * than no link at all.
   */
  nav: [
    { label: 'Features', to: '/features' },
    { label: 'Pricing', to: '/pricing' },
    { label: 'Compare', to: '/compare' },
    { label: 'Partners', to: '/partners' },
    { label: 'Contact', to: '/contact' },
  ] as { label: string; to: string }[],

  /** Footer link groups. Also empty until you have somewhere to point. */
  footer: [
    {
      heading: 'Company',
      links: [
        { label: 'Features', to: '/features' },
        { label: 'Pricing', to: '/pricing' },
        { label: 'Compare', to: '/compare' },
        { label: 'Partners', to: '/partners' },
        { label: 'About', to: '/about' },
        { label: 'Contact', to: '/contact' },
      ],
    },
    {
      heading: 'Legal',
      links: [
        { label: 'Privacy', to: '/privacy' },
        { label: 'Terms', to: '/terms' },
        { label: 'Refunds', to: '/refund' },
        { label: 'Cancellation', to: '/cancellation' },
      ],
    },
  ] as { heading: string; links: { label: string; to: string }[] }[],

  social: {
    github: 'https://github.com',
  },
} as const;

export type BrandNavItem = (typeof brand.nav)[number];
